import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { Source } from '../../models/Source'
import { useOnboardingContext } from '../../providers/OnboardingProvider'
import { Spinner } from '../Spinner'
import Button from '../app/design/Button'
import Paragraph from '../app/design/Paragraph'
import { BankModal } from '../payments/BankModal'
import { CardModal } from '../payments/CardModal'
import { WorkflowPage } from '../workflows/WorkflowPage'

export function SetupDashboardPayoutPage() {
    const [sources, setSources] = useState<Source[]>([])

    const { data, isLoading } = useQuery({
        queryKey: ['onboarding', 'gl_payout'],
        queryFn: async () => {
            const sources = await Source.validSources()
            setSources(sources)
            return { sources }
        },
    })

    const navigate = usePSNavigate()
    const { advanceOnboardingState } = useOnboardingContext()
    const queryClient = useQueryClient()

    const [modal, setModal] = useState<'checking' | 'card' | null>(null)

    const isCheckingAccountAdded = !!sources.find((source) =>
        ['checking', 'savings'].includes(source.type ?? '')
    )
    const isPaymentCardAdded = !!sources.find((source) =>
        ['credit', 'debit', 'prepaid'].includes(source.type ?? '')
    )
    const saveEnabled = isCheckingAccountAdded && isPaymentCardAdded

    const onNextHandler = async () => {
        await advanceOnboardingState()
    }

    if (isLoading || !data) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <Spinner />
            </div>
        )
    }

    return (
        <>
            <WorkflowPage
                title="Recieve payouts"
                onClose={() => navigate('/')}
                footerContent={
                    <div className="flex justify-end gap-x-4">
                        <Button intent="secondary" type="submit" onClick={onNextHandler}>
                            Skip
                        </Button>
                        <Button
                            intent="primary"
                            type="submit"
                            // isLoading={isSaving}
                            disabled={!saveEnabled}
                            onClick={onNextHandler}
                        >
                            Next
                        </Button>
                    </div>
                }
            >
                <Paragraph size="md" className="mt-4 text-center text-gray-600">
                    Tell us where you’d like to receive your payments & process refunds
                </Paragraph>

                <WorkflowPage.Card className="mt-12 gap-y-3">
                    <WorkflowPage.IconSection
                        className="rounded-lg border border-gray-300 p-4"
                        title="Checking account"
                        icon="bank.large"
                        description="Add a checking account. Bank transfers to a checking account take 1 business day"
                        hasHoverEffect
                        infoTag={isCheckingAccountAdded ? 'Added' : 'Required'}
                        tagIntent={isCheckingAccountAdded ? 'success' : 'muted'}
                        secondaryView={
                            <Button
                                intent="tertiaryLink"
                                className="whitespace-nowrap"
                                size="small"
                                onClick={() => setModal('checking')}
                            >
                                {isCheckingAccountAdded ? 'Add another' : 'Add'}
                            </Button>
                        }
                    />
                    <WorkflowPage.IconSection
                        className="rounded-lg border border-gray-300 p-4"
                        title="Payment card"
                        icon="bankCard.large"
                        description="Add a debit card. Instant transfers to a debit card take 15 minutes"
                        hasHoverEffect
                        infoTag={isPaymentCardAdded ? 'Added' : 'Required'}
                        tagIntent={isPaymentCardAdded ? 'success' : 'muted'}
                        secondaryView={
                            <Button
                                intent="tertiaryLink"
                                className="whitespace-nowrap"
                                size="small"
                                onClick={() => setModal('card')}
                            >
                                {isPaymentCardAdded ? 'Add another' : 'Add'}
                            </Button>
                        }
                    />
                </WorkflowPage.Card>
            </WorkflowPage>
            {modal === 'card' && (
                <CardModal
                    isOpen={modal === 'card'}
                    onClose={() => setModal(null)}
                    onSuccess={{
                        action: 'create-source',
                        client: undefined,
                        callback: (source) => {
                            setSources([...sources, source])
                            setModal(null)
                        },
                    }}
                />
            )}
            {modal === 'checking' && (
                <BankModal
                    isOpen={modal === 'checking'}
                    onClose={() => setModal(null)}
                    onSuccess={(source) => {
                        queryClient.invalidateQueries({ queryKey: ['dashboard'] })
                        setSources([...sources, source])
                        setModal(null)
                    }}
                />
            )}
        </>
    )
}
